import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StepLayout from '../../components/StepLayout/StepLayout';
import { useTunnelDispatch, useTunnelState } from '../../context/TunnelContext';
import { DELIVERY_FEE, GUMS, SERVICES } from '../../utils/mockData';

export default function OrderSummary() {
  const navigate = useNavigate();
  const dispatch = useTunnelDispatch();
  const { shoes, client } = useTunnelState();

  const subtotal = shoes.reduce(
    (sum, shoe) => sum + (SERVICES.find((service) => service.id === shoe.service)?.price ?? 0),
    0
  );
  const total = subtotal + DELIVERY_FEE;

  useEffect(() => {
    if (shoes.length === 0 || !client.email) {
      navigate('/nouvelle-demande/marque', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleRemoveShoe(index) {
    dispatch({ type: 'REMOVE_SHOE', index });
  }

  function handleConfirm() {
    dispatch({ type: 'CONFIRM_ORDER' });
    navigate('/nouvelle-demande/tickets');
  }

  function handleCancelOrder() {
    dispatch({ type: 'RESET_ORDER' });
    navigate('/');
  }

  return (
    <StepLayout title="Récapitulatif de la commande">
      {shoes.length > 0 ? (
        <Stack spacing={2} sx={{ maxWidth: 640, mb: 3 }}>
          {shoes.map((shoe, index) => (
            <Card key={index} variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle1">
                    Paire {index + 1} — {shoe.brand}
                    {shoe.model ? ` (${shoe.model})` : ''}
                  </Typography>
                  <IconButton
                    aria-label="Supprimer cette paire"
                    onClick={() => handleRemoveShoe(index)}
                    size="small"
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
                <Typography variant="body2">Pointure : {shoe.size}</Typography>
                <Typography variant="body2">
                  Gomme : {GUMS.find((gum) => gum.id === shoe.gum)?.label}
                </Typography>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">
                    Service : {SERVICES.find((service) => service.id === shoe.service)?.label}
                  </Typography>
                  <Typography variant="body2">
                    {SERVICES.find((service) => service.id === shoe.service)?.price} €
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Stack spacing={2} sx={{ maxWidth: 480, mb: 4 }}>
          <Typography color="text.secondary">
            Votre commande ne contient plus aucune paire.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/nouvelle-demande/marque')}
          >
            Ajouter une paire
          </Button>
        </Stack>
      )}

      <Divider sx={{ maxWidth: 640, mb: 2 }} />

      <Stack spacing={0.5} sx={{ maxWidth: 640, mb: 4 }}>
        <Typography variant="subtitle1">Vos coordonnées</Typography>
        <Typography variant="body2">
          {client.givenName} {client.familyName}
        </Typography>
        <Typography variant="body2">{client.email}</Typography>
        <Typography variant="body2">{client.phone}</Typography>
      </Stack>

      {shoes.length > 0 && (
        <>
          <Divider sx={{ maxWidth: 640, mb: 2 }} />

          <Stack spacing={0.5} sx={{ maxWidth: 640, mb: 4 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Sous-total</Typography>
              <Typography variant="body2">{subtotal} €</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Frais de livraison</Typography>
              <Typography variant="body2">{DELIVERY_FEE} €</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1">Total</Typography>
              <Typography variant="subtitle1">{total} €</Typography>
            </Stack>
          </Stack>
        </>
      )}

      <Stack spacing={2} sx={{ maxWidth: 480 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleConfirm}
          disabled={shoes.length === 0}
        >
          Confirmer la commande
        </Button>
        <Button variant="text" color="error" size="large" onClick={handleCancelOrder}>
          Annuler la commande
        </Button>
      </Stack>
    </StepLayout>
  );
}
